import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { UploadJob } from './indexed-db.interface';
import { BehaviorSubject, map, Observable, of, switchMap, tap } from 'rxjs';
import { JobStatus } from '@dal/async-upload';

@Injectable({ providedIn: 'root' })
export class IndexedDBService {
  private jobsSubject = new BehaviorSubject<UploadJob[] | null>(null);
  jobs$ = this.jobsSubject.asObservable();
  constructor(private db: NgxIndexedDBService) { }

  save(job:UploadJob) {
    return this.db.add('jobs', job).pipe(
      tap(() => this.refreshJobs())
    );
  }

  get(jobId: string) {
    return this.db.getByKey<UploadJob>('jobs', jobId).pipe(
      tap(() => this.refreshJobs())
    );
  }

  getByUser(userId: string) {
    return this.db.getAll<UploadJob>('jobs').pipe(map(jobs => jobs.filter(j => j.userId === userId)));
  }
  
  getAll() {
    return this.db.getAll<UploadJob>('jobs').pipe(
      tap(() => this.refreshJobs())
    );
  }

  update(job: UploadJob) {
    return this.db.update('jobs', job).pipe(
      tap(() => this.refreshJobs())
    );
  }

  updateFileProgress(jobId: string, fileName: string, status: string): Observable<UploadJob> {
    console.log(`Updating file progress for job ${jobId}, file ${fileName}, status ${status}`);

    return this.db.getByKey<UploadJob>('jobs', jobId).pipe(
      switchMap((job: UploadJob | undefined) => {
        if (!job) {
          throw new Error('Job not found');
        }
        if (!job.uploadedFiles) {
          job.uploadedFiles = [];
        }
        if (!job.uploadedFiles.some((file => file.name === fileName))) {
          job.uploadedFiles.push({ name: fileName, status });
          job.completion = Math.floor((job.uploadedFiles.length / job.files.length) * 100);
        }
        console.log(`Job before update:`, job);

        return this.db.update('jobs', job).pipe(map(() => job));
      }),
      tap(() => this.refreshJobs())
    );
  }



  delete(jobId: string) {
    return this.db.delete('jobs', jobId).pipe(
      tap(() => this.refreshJobs())
    );
  }

  refreshJobs() {
    this.getAll().subscribe(jobs => this.jobsSubject.next(jobs));
  }

  getRetryableJobs(maxRetries = 3) {
    return this.db.getAll<UploadJob>('jobs').pipe(map(jobs =>
      jobs.filter(job => job.status === JobStatus.FAILED && (job.retries ?? 0) < maxRetries)
    ));
  }

  pauseJob(jobId: string): Observable<UploadJob> {
    return this.db.getByKey<UploadJob>('jobs', jobId).pipe(
      switchMap((job: UploadJob | undefined) => {
        if (!job) {
          throw new Error('Job not found');
        }

        // Only allow pausing of pending or uploading jobs
        if (job.status !== JobStatus.PENDING && job.status !== JobStatus.UPLOADING) {
          return of(job);
        }

        job.status = JobStatus.PAUSED;
        job.lastModified = Date.now();

        return this.db.update('jobs', job).pipe(map(() => job));
      }),
      tap(() => this.refreshJobs())
    );
  }

  resumeJob(jobId: string): Observable<UploadJob> {
    return this.db.getByKey<UploadJob>('jobs', jobId).pipe(
      switchMap((job: UploadJob | undefined) => {
        if (!job) {
          throw new Error('Job not found');
        }

        // Only allow resuming paused jobs
        if (job.status !== JobStatus.PAUSED) {
          return of(job);
        }

        job.status = JobStatus.PENDING;
        job.lastModified = Date.now();

        return this.db.update('jobs', job).pipe(map(() => job));
      }),
      tap(() => this.refreshJobs())
    );
  }

  cancelJob(jobId: string): Observable<UploadJob> {
    return this.db.getByKey<UploadJob>('jobs', jobId).pipe(
      switchMap((job: UploadJob | undefined) => {
        if (!job) {
          throw new Error('Job not found');
        }

        // Don't cancel already completed jobs
        if (job.status === JobStatus.COMPLETE) {
          return of(job);
        }

        job.status = JobStatus.CANCELED;
        job.lastModified = Date.now();

        return this.db.update('jobs', job).pipe(map(() => job));
      }),
      tap(() => this.refreshJobs())
    );
  }
}
